/**
 * User-facing help content. Kept as a data structure (not embedded JSX) so
 * it's easy to edit, can be searched in the future, and can eventually be
 * generated from markdown if needed.
 */

export interface HelpSection {
  id: string
  title: string
  blurb: string
  steps?: string[]
  tips?: string[]
}

export interface HelpModule {
  id: string
  title: string
  emoji: string
  description: string
  sections: HelpSection[]
}

export const HELP_MODULES: HelpModule[] = [
  {
    id: 'overview',
    title: 'Quick Start',
    emoji: '🚀',
    description: 'A 60-second tour of DebtOS.',
    sections: [
      {
        id: 'what-is-it',
        title: 'What DebtOS is',
        blurb:
          'DebtOS is a personal debt optimization dashboard. It helps you understand your debts, choose the best payoff strategy, and balance your monthly cash flow against reality. Everything runs locally — your financial data never leaves this device.',
      },
      {
        id: 'four-modules',
        title: 'The four modules',
        blurb: 'Use the tabs at the top to switch between:',
        steps: [
          'Dashboard — high-level snapshot of your portfolio',
          'Debt Registry — the source of truth for every debt you owe',
          'Strategy Engine — compares 4 payoff strategies and projects the future',
          'Cash Flow — your monthly "balance the books" workflow with income, expenses, and allocations',
        ],
      },
      {
        id: 'first-steps',
        title: 'Recommended first steps',
        blurb: "If you're new to DebtOS:",
        steps: [
          '1. Go to Debt Registry and review (or replace) the seeded debts with yours',
          '2. Open the Strategy Engine to see how long payoff takes at different extra payments',
          '3. Switch to Cash Flow, add your income sources, set essentials, and try Apply Avalanche',
          "4. Once you've made your real payments for the month, click Close Month",
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    emoji: '📊',
    description: 'Your high-level financial snapshot.',
    sections: [
      {
        id: 'summary-cards',
        title: 'Summary cards',
        blurb:
          "Four KPIs that summarize your debt: Total Debt, Monthly Minimums (sum of all required minimums), Weighted Avg APR (balance-weighted, not arithmetic), and Monthly Interest Burn (how much you're paying in interest each month at the current state).",
      },
      {
        id: 'debt-breakdown',
        title: 'Debt Breakdown pie',
        blurb:
          'Donut chart of your top 8 debts by balance, colored by APR severity (red = critical ≥20%, orange = high ≥10%, yellow = medium ≥5%, green = low <5%, blue = promo, cyan = 0% promo).',
      },
      {
        id: 'top-burners',
        title: 'Top Interest Burners',
        blurb:
          'The 5 debts costing you the most in interest right now. Uses your effective APR — promo debts at 0% show $0 burn until they expire.',
      },
      {
        id: 'strategy-snapshot',
        title: 'Strategy Snapshot',
        blurb:
          'Quick comparison of all 4 payoff strategies: months to debt-free, payoff date, total interest, savings vs minimum-only baseline.',
      },
      {
        id: 'promo-banner',
        title: 'Promo alert banner',
        blurb:
          'Red banner at the top when one of your promos has expired or is critical (≤3 months). Click the X on each alert to dismiss it. The bell icon in the header shows all active alerts plus a history of dismissed ones.',
      },
    ],
  },
  {
    id: 'registry',
    title: 'Debt Registry',
    emoji: '📋',
    description: 'The source of truth for every debt.',
    sections: [
      {
        id: 'add-debt',
        title: 'Adding a debt',
        blurb: 'Click the green "Add Debt" button at the top right.',
        steps: [
          'Fill in name, lender, type, balance, APR, and minimum payment',
          'Save — it appears in the table immediately and feeds into all calculations',
        ],
      },
      {
        id: 'sort-filter',
        title: 'Sort and filter',
        blurb:
          'Click any column header to sort. Click again to reverse. Use the search bar to filter by name or lender. Use the chip filters to quickly show critical APR, high APR, debts with promos, or debts with BT offers.',
      },
      {
        id: 'edit-debt',
        title: 'Editing a debt',
        blurb: 'Click the pencil icon on any row.',
        steps: [
          'Update the basic fields (balance, APR, minimum)',
          'Expand "Promo Rate" to add or edit a promotional APR with expiration date',
          'When a promo expires, click "Transition to Standard APR" to apply the post-promo rate and clear the promo entirely',
          'Expand "Balance Transfer Offer" to track an offer (APR, fee, expiration, credit limit)',
        ],
      },
      {
        id: 'delete-debt',
        title: 'Deleting a debt',
        blurb:
          "Click the red trash icon. You'll be asked to confirm. Note: deleting a debt does not affect closed monthly ledger entries — historical accuracy is preserved.",
      },
    ],
  },
  {
    id: 'engine',
    title: 'Strategy Engine',
    emoji: '⚡',
    description: 'Compare payoff strategies and project the future.',
    sections: [
      {
        id: 'four-strategies',
        title: 'The four strategies',
        blurb: 'DebtOS simulates four ways to pay off debt:',
        steps: [
          'Avalanche — highest APR first (mathematically optimal)',
          'Snowball — lowest balance first (psychologically motivating)',
          'Hybrid — APR × balance (largest absolute interest first)',
          'Minimum Only — pay only minimums (the baseline danger zone)',
        ],
      },
      {
        id: 'extra-payment',
        title: 'Extra Monthly Payment slider',
        blurb:
          'Drag the slider to add extra dollars on top of all minimums. The simulations re-run instantly. You can also type any number directly. For best accuracy, enter your real surplus from the Cash Flow tab.',
      },
      {
        id: 'strategy-cards',
        title: 'Strategy cards',
        blurb:
          "Click any card to select that strategy. The Attack Order panel below updates to show that strategy's priority sequence. The selected card gets a colored border.",
      },
      {
        id: 'attack-order',
        title: 'Attack Order panel',
        blurb:
          'Shows which debt the selected strategy is targeting first, second, third, etc. The first debt in the list is your "current target" — focus extra payments there.',
      },
      {
        id: 'why-minimum-infinite',
        title: 'Why does Minimum Only show ∞?',
        blurb:
          "For some portfolios, at least one debt has a minimum payment lower than its monthly interest accrual. The balance grows every month. With strict minimums alone, you never pay it off. The engine is correctly showing this — it's a warning, not a bug.",
      },
    ],
  },
  {
    id: 'cashflow',
    title: 'Cash Flow',
    emoji: '💰',
    description: 'Balance your monthly "balance sheet" of debt.',
    sections: [
      {
        id: 'monthly-workflow',
        title: 'The monthly workflow',
        blurb:
          'This is where strategy meets reality. Each month you record your actual income and decide how to allocate it across your debts.',
        steps: [
          '1. Set up income sources (one-time, then reused each month)',
          '2. At the start of the month, enter actual income amounts',
          '3. Set essential expenses (rent, food, utilities) — used for the survival floor',
          '4. Adjust planned payments per debt, or click Apply Avalanche / Snowball to auto-fill',
          '5. After making the payments in real life, click Close Month to lock the entry',
        ],
      },
      {
        id: 'income-sources',
        title: 'Income Sources',
        blurb:
          'Define each income source once with type (salary, business, rental, investment, windfall, other) and stability (guaranteed, expected, variable). Examples: W2 salary, rental income, side business, gambling winnings, stock market profits.',
      },
      {
        id: 'survival-floor',
        title: 'Survival Floor',
        blurb:
          'The most important number in this module: minimum required income to make all debt minimums and cover essentials. If your income falls below this, you go backwards. The card lights up red (deficit), amber (less than 10% cushion), or green (safe).',
      },
      {
        id: 'required-min',
        title: 'Required Min column (per-month override)',
        blurb:
          'Each row shows the minimum the lender required this month. Defaults to the registry value. If your statement shows a different number (lenders adjust monthlies), edit this directly. The override is highlighted in blue and is captured for historical accuracy.',
      },
      {
        id: 'quick-fill',
        title: 'Quick Fill bar',
        blurb:
          'Three buttons to populate the allocation table fast: Minimums Only resets everything, Apply Avalanche pours surplus into highest APR, Apply Snowball pours surplus into lowest balance. Surplus auto-calculated from income minus essentials minus minimums.',
      },
      {
        id: 'close-month',
        title: 'Closing a month',
        blurb:
          "When the month is done and your real payments are made, click Close Month. This reconciles balances (auto-calculated unless you override per debt), locks the entry, and creates next month's entry with values copied forward.",
      },
      {
        id: 'start-next-month',
        title: 'Starting next month early',
        blurb:
          "Click Start Next Month any time to create the next month's plan without closing the current one. Useful if you want to plan ahead.",
      },
      {
        id: 'reopen-month',
        title: 'Reopening a closed month',
        blurb:
          'Mistakes happen. Navigate to a closed month and click Reopen Month to make corrections. Use sparingly — historical accuracy is important for the actual-vs-projected trajectory feature.',
      },
      {
        id: 'history-tab',
        title: 'History tab',
        blurb:
          'Read-only view of every closed month. Shows monthly totals (income, paid, interest, principal reduction, ending balance), YTD aggregates, and your survival floor streak. Click any row to expand and see the full per-debt allocation breakdown.',
      },
      {
        id: 'streak-tracker',
        title: 'Survival Floor Streak',
        blurb:
          'Tracks consecutive closed months where your income covered the survival floor (minimums + essentials). Resets when a month falls below. The "longest streak" is preserved across resets — chase your record.',
      },
      {
        id: 'projections-tab',
        title: 'Projections tab — the killer feature',
        blurb:
          'The Actual vs Projected trajectory chart overlays your real history (white line, from closed entries) against the engine simulation (dashed colored line, from your selected strategy). The gap between them is your accountability — are you on track, ahead, or behind?',
      },
      {
        id: 'projections-divergence',
        title: 'Reading the divergence',
        blurb:
          "If your actual line is BELOW the projected line, you're ahead of schedule (less debt than predicted). If above, you're behind. The divergence card at the top of the Projections tab tells you exactly how much.",
      },
      {
        id: 'projections-strategy-switch',
        title: 'Switching strategies on the chart',
        blurb:
          'The Projections chart uses whatever strategy you have selected in the Strategy Engine. To compare what your trajectory would look like under a different strategy, click any of the four strategy cards at the bottom of the Projections tab.',
      },
    ],
  },
  {
    id: 'tips',
    title: 'Tips & Concepts',
    emoji: '💡',
    description: 'Things worth knowing.',
    sections: [
      {
        id: 'apr-severity',
        title: 'APR severity colors',
        blurb:
          'Red = critical (≥20%) — credit card territory. Orange = high (10-20%) — installment loans. Yellow = medium (5-10%). Green = low (<5%). Blue = promo. Cyan = 0% promo.',
      },
      {
        id: 'effective-vs-base',
        title: 'Effective APR vs base APR',
        blurb:
          'A debt with a 0% promo has TWO APRs: the promo APR (0%) and the post-promo APR (e.g. 23.74%). DebtOS tracks both. Calculations use whichever is currently effective. When the promo expires, calculations switch automatically — but you should also "transition" the debt in the registry to clear the promo metadata.',
      },
      {
        id: 'data-storage',
        title: 'Where is my data stored?',
        blurb:
          "Everything is in your browser's localStorage. Nothing leaves your device. Clearing browser data will erase your debts, ledger, income sources, and notifications. There's no cloud backup yet — that comes in Phase 2 (SaaS mode).",
      },
      {
        id: 'keyboard',
        title: 'Keyboard shortcuts',
        blurb: 'None yet. Will be added in a future polish pass.',
      },
    ],
  },
]
